/* eslint-disable jsdoc/require-jsdoc */
import { ReactElement } from 'react';
import { Plus, X as CloseIcon, Trash2 } from 'lucide-react';
import { ChatSummary, formatRelative } from '../../services/chat-storage';

interface Props {
  open: boolean;
  chats: ChatSummary[];
  currentChatId: string;
  onSelect: (id: string) => void;
  onNew: () => void;
  onDelete: (id: string) => void;
  onClose: () => void;
}

// Slide-out drawer listing saved chats. Backed by `chat-storage`, which
// persists chats to the user's account (server-side), so they follow the
// user across browsers and devices.
function ChatSidebar({
  open,
  chats,
  currentChatId,
  onSelect,
  onNew,
  onDelete,
  onClose
}: Props): ReactElement {
  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-40 bg-gray-900/70 backdrop-blur-sm transition-opacity ${
          open ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
        onClick={onClose}
        aria-hidden
      />
      {/* Drawer */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 flex w-80 max-w-[85vw] flex-col border-r border-gray-800 bg-gray-900 shadow-2xl transition-transform ${
          open ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between border-b border-gray-800 px-4 py-3">
          <div>
            <div className="text-sm uppercase tracking-wide text-gray-500">Insight Agent</div>
            <div className="text-lg font-semibold text-white">Chats</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            title="Close"
            className="rounded-md p-1.5 text-gray-500 transition hover:bg-gray-800 hover:text-white"
          >
            <CloseIcon className="h-5 w-5" />
          </button>
        </div>

        <button
          type="button"
          onClick={onNew}
          className="mx-3 mt-3 flex items-center justify-center gap-2 rounded-lg border border-primary-500/40 bg-primary-500/10 px-3 py-2.5 text-sm font-medium text-primary-200 transition hover:border-primary-500/60 hover:bg-primary-500/15 hover:text-white"
        >
          <Plus className="h-4 w-4" />
          New chat
        </button>

        <div className="mt-2 flex-1 overflow-y-auto">
          {chats.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-gray-500">
              No saved chats yet. Start a conversation — it will appear here.
            </div>
          ) : (
            <ul className="space-y-0.5 p-1.5">
              {chats.map((c) => {
                const active = c.id === currentChatId;
                return (
                  <li
                    key={c.id}
                    className={`group flex items-stretch rounded-lg transition ${
                      active ? 'bg-gray-800' : 'hover:bg-gray-800/70'
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => onSelect(c.id)}
                      className="min-w-0 flex-1 px-3 py-2.5 text-left"
                    >
                      <div className="truncate text-sm font-medium text-gray-100">
                        {c.title}
                      </div>
                      <div className="mt-0.5 text-xs text-gray-500">
                        {formatRelative(c.updatedAt)} · {c.messageCount} msg
                      </div>
                    </button>
                    <button
                      type="button"
                      // eslint-disable-next-line no-alert
                      onClick={() => {
                        if (window.confirm(`Delete "${c.title}"?`)) onDelete(c.id);
                      }}
                      title="Delete chat"
                      className="px-2 text-gray-600 opacity-0 transition group-hover:opacity-100 hover:text-error-500"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="border-t border-gray-800 px-4 py-2 text-xs text-gray-600">
          {chats.length} saved · synced to your account
        </div>
      </aside>
    </>
  );
}

export default ChatSidebar;
